import { useState, useCallback } from 'react'
import { solutionRequirementsApi } from '@/lib/api/client'
import { useEntityCrudListener } from '@/hooks/useEntityListener'
import type { RequirementsTree, RequirementLens } from '@ispot/shared'

export type GeneratingTier = 'vision' | 'prd' | 'specs' | 'all' | null

export function useSolutionRequirements(accountId: string, projectId: string) {
  const [tree, setTree] = useState<RequirementsTree | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [generatingTier, setGeneratingTier] = useState<GeneratingTier>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeLens, setActiveLens] = useState<RequirementLens>('vision')

  const isGenerating = generatingTier !== null

  const loadTree = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await solutionRequirementsApi.getTree(accountId, projectId)
      setTree(data)
    } catch (err) {
      setError('Failed to load requirements')
    } finally {
      setIsLoading(false)
    }
  }, [accountId, projectId])

  // Auto-refresh when Features/Capabilities/Requirements change (from any source)
  useEntityCrudListener('Feature', useCallback(() => loadTree(), [loadTree]))
  useEntityCrudListener('Capability', useCallback(() => loadTree(), [loadTree]))
  useEntityCrudListener('Requirement', useCallback(() => loadTree(), [loadTree]))

  const ensureDefault = useCallback(async () => {
    try {
      await solutionRequirementsApi.ensureDefault(accountId, projectId)
      await loadTree()
    } catch (err) {
      setError('Failed to initialize requirements')
    }
  }, [accountId, projectId, loadTree])

  const generateFromGraph = useCallback(async () => {
    setGeneratingTier('all')
    setError(null)
    try {
      const data = await solutionRequirementsApi.generate(accountId, projectId)
      if (data?.error) { setError(data.error); return }
      if (data?.vision) { setTree(data) } else { setError('No evidence found. Upload interviews or documents first.') }
    } catch (err: any) {
      const msg = err?.message || err?.error || 'Generation failed. Try again.'
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg))
    } finally {
      setGeneratingTier(null)
    }
  }, [accountId, projectId])

  const generateVision = useCallback(async () => {
    setGeneratingTier('vision')
    setError(null)
    try {
      const data = await solutionRequirementsApi.generateVision(accountId, projectId)
      if (data?.error) { setError(data.error); return }
      if (data?.tree) { setTree(data.tree) }
    } catch (err) {
      setError('Vision generation failed. Try again.')
    } finally {
      setGeneratingTier(null)
    }
  }, [accountId, projectId])

  const generatePrd = useCallback(async () => {
    setGeneratingTier('prd')
    setError(null)
    try {
      const data = await solutionRequirementsApi.generatePrd(accountId, projectId)
      if (data?.error) { setError(data.error); return }
      if (data?.tree) { setTree(data.tree) }
    } catch (err) {
      setError('PRD generation failed. Try again.')
    } finally {
      setGeneratingTier(null)
    }
  }, [accountId, projectId])

  const generateSpecs = useCallback(async () => {
    setGeneratingTier('specs')
    setError(null)
    try {
      const data = await solutionRequirementsApi.generateSpecs(accountId, projectId)
      if (data?.error) { setError(data.error); return }
      if (data?.tree) { setTree(data.tree) }
    } catch (err) {
      setError('Specs generation failed. Try again.')
    } finally {
      setGeneratingTier(null)
    }
  }, [accountId, projectId])

  const createVision = useCallback(async (name: string, visionStatement?: string) => {
    await solutionRequirementsApi.createVision(accountId, projectId, { name, visionStatement })
    await loadTree()
  }, [accountId, projectId, loadTree])

  const updateVision = useCallback(async (visionId: string, data: Record<string, unknown>) => {
    await solutionRequirementsApi.updateVision(accountId, projectId, visionId, data)
    await loadTree()
  }, [accountId, projectId, loadTree])

  const createCapability = useCallback(async (visionId: string, name: string, description?: string) => {
    await solutionRequirementsApi.createCapability(accountId, projectId, visionId, { name, description })
    await loadTree()
  }, [accountId, projectId, loadTree])

  const updateCapability = useCallback(async (capabilityId: string, data: Record<string, unknown>) => {
    await solutionRequirementsApi.updateCapability(accountId, projectId, capabilityId, data)
    await loadTree()
  }, [accountId, projectId, loadTree])

  const createFeature = useCallback(async (capabilityId: string, data: Record<string, unknown>) => {
    await solutionRequirementsApi.createFeature(accountId, projectId, capabilityId, data)
    await loadTree()
  }, [accountId, projectId, loadTree])

  const updateFeature = useCallback(async (featureId: string, data: Record<string, unknown>) => {
    await solutionRequirementsApi.updateFeature(accountId, projectId, featureId, data)
    await loadTree()
  }, [accountId, projectId, loadTree])

  const deleteFeature = useCallback(async (featureId: string) => {
    await solutionRequirementsApi.deleteFeature(accountId, projectId, featureId)
    await loadTree()
  }, [accountId, projectId, loadTree])

  const linkEvidence = useCallback(async (featureId: string, data: { type: string; uri: string; label: string; severity?: string; confidence?: number }) => {
    await solutionRequirementsApi.linkEvidence(accountId, projectId, featureId, data)
    await loadTree()
  }, [accountId, projectId, loadTree])

  const exportMarkdown = useCallback(async (tier: RequirementLens) => {
    return solutionRequirementsApi.exportMarkdown(accountId, projectId, tier)
  }, [accountId, projectId])

  return {
    tree, isLoading, isGenerating, generatingTier, error, activeLens,
    setActiveLens,
    loadTree, ensureDefault, generateFromGraph,
    generateVision, generatePrd, generateSpecs,
    createVision, updateVision,
    createCapability, updateCapability,
    createFeature, updateFeature, deleteFeature,
    linkEvidence, exportMarkdown,
  }
}
